import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Form, FormGroup, FormText, Input, Label, Spinner } from 'reactstrap';
import InputMask from 'react-input-mask';
import parsePhoneNumber from 'libphonenumber-js';
import intl from 'react-intl-universal';
import moment from 'moment';
import * as yup from 'yup';

import Icon from 'components/Icon';
import Page from 'components/Page';
import PageHeader from 'components/PageHeader';
import PageFooter from 'components/PageFooter';
import PhoneInput from 'components/PhoneInput';
import Select from 'components/Selectes/SimpleSelect';
import * as ActionTypes from 'modules/forms/actions/form.actions';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { DATE_SERVER_FORMAT, DOB_FORMAT, UK_POSTCODE_REGEX } from 'constants/app.constants';
import { EGender, IPatientView, EUserPhoneType } from 'interfaces/patients.interfaces';
import { patchCustomer, signOutPatient } from 'modules/auth/actions/auth.actions';

type TFormValues = IPatientView & { phoneType: EUserPhoneType; phoneNumber: string };

const PatientInfo = (): JSX.Element => {
  const dispatch = useDispatch();

  const customer = useTypedSelector(state => state.auth.patient?.customer);
  const isLoading = useTypedSelector(state => state.auth.loading.patchCustomer);
  const formUuid = useTypedSelector(state => state.form.formUuid);

  const initialValues: Partial<TFormValues> = useMemo(
    () => ({
      firstName: '',
      lastName: '',
      dob: '',
      gender: undefined,
      address: undefined,
      nhs: '',
      email: '',
      phoneNumber: '+44',
      phoneType: EUserPhoneType.MOBILE,
      // surgery: undefined,
    }),
    [],
  );

  const validationSchema = useMemo(
    () =>
      yup
        .object({
          firstName: yup.string().required(intl.get(`common.validate.simpleRequired`)).trim(),
          lastName: yup.string().required(intl.get(`common.validate.simpleRequired`)).trim(),
          dob: yup.mixed().when('$customer', {
            is: (customerData: any) => !!customerData?.dob,
            then: yup.string().trim(),
            otherwise: yup
              .string()
              .trim()
              .required(intl.get(`common.validate.simpleRequired`))
              .test(
                'dob',
                () => intl.get(`common.validate.invalidDate`),
                value => moment(value, DOB_FORMAT, true).isValid(),
              )
              .test(
                'dob',
                () => intl.get(`common.validate.invalidDob`),
                value => moment(value, DOB_FORMAT, true).isBefore(moment()),
              ),
          }),
          gender: yup.string().required(intl.get(`common.validate.simpleRequired`)).trim(),
          nhs: yup.mixed().when('$customer', {
            is: (customerData: any) => !!customerData?.nhs,
            then: yup.string().trim(),
            otherwise: yup.mixed().when({
              is: (val: string | number) => val !== '' && val !== undefined,
              then: yup
                .string()
                .trim()
                .typeError(intl.get(`common.validate.invalidNHSNumber`))
                .matches(/^\d{10}$/g, intl.get(`common.validate.invalidNHSNumber`)),
              otherwise: yup.string().trim(),
            }),
          }),
          address: yup
            .object()
            .default(undefined)
            .shape({
              streetAddress: yup.mixed().when('$customer', {
                is: (customerData: any) => !!customerData?.address?.streetAddress,
                then: yup.string().trim(),
                otherwise: yup.string().trim().required(intl.get(`common.validate.simpleRequired`)),
              }),
              address2: yup.string().trim(),
              suit: yup.string().trim(),
              country: yup.mixed().when('$customer', {
                is: (customerData: any) => !!customerData?.address?.country,
                then: yup.string().trim(),
                otherwise: yup.string().trim().required(intl.get(`common.validate.simpleRequired`)),
              }),
              city: yup.mixed().when('$customer', {
                is: (customerData: any) => !!customerData?.address?.city,
                then: yup.string().trim(),
                otherwise: yup.string().trim().required(intl.get(`common.validate.simpleRequired`)),
              }),
              postalCode: yup.mixed().when('$customer', {
                is: (customerData: any) => !!customerData?.address?.postalCode,
                then: yup.string().trim(),
                otherwise: yup
                  .string()
                  .trim()
                  .required(intl.get(`common.validate.simpleRequired`))
                  .matches(UK_POSTCODE_REGEX, intl.get('common.validate.invalidPostalCodeFormat')),
              }),
            }),
          email: yup.mixed().when('$customer', {
            is: (customerData: any) => !!customerData?.email,
            then: yup.string().trim(),
            otherwise: yup
              .string()
              .trim()
              .required(intl.get(`common.validate.simpleRequired`))
              .email(intl.get(`common.validate.emailWrongFormat`)),
          }),
          phoneNumber: yup.mixed().when('$customer', {
            is: (customerData: any) => !!customerData?.mobilePhone || !!customerData?.landlinePhone,
            then: yup.string().trim(),
            otherwise: yup
              .string()
              .trim()
              .required(intl.get(`common.validate.simpleRequired`))
              .test(
                'is-phone',
                () => intl.get('common.validate.invalidPhoneNumber'),
                value => {
                  if (!value) return true;
                  try {
                    const phoneNumber = parsePhoneNumber(value);
                    return phoneNumber ? phoneNumber.isValid() : false;
                  } catch {
                    return false;
                  }
                },
              ),
          }),
          // surgery: yup.mixed().when('$formUuid', {
          //   is: (val: string) =>
          //     [FORM_UUID_WEIGHT_LOSS_CLINIC, FORM_UUID_GENERAL_PRESCREENING].includes(val),
          //   then: yup.mixed().required(intl.get(`common.validate.simpleRequired`)),
          //   otherwise: yup.mixed().notRequired(),
          // }),
        })
        .required(),
    [],
  );

  const mainRef = useRef<HTMLDivElement>(null);
  const [isScrolledFromTop, setScrolledFromTop] = useState(false);

  useEffect(() => {
    const mainEl = mainRef.current;

    const handleScroll = () => {
      if (mainEl) {
        const { scrollTop } = mainEl;

        const scrolledFromTop = scrollTop > 0;

        setScrolledFromTop(scrolledFromTop);
      }
    };

    setTimeout(() => {
      handleScroll();
    }, 50);
    mainEl?.addEventListener('scroll', handleScroll);

    return () => {
      mainEl?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const formMethods = useForm<TFormValues>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
    context: { formUuid, customer },
  });

  const { control, reset, handleSubmit } = formMethods;

  useEffect(() => {
    if (customer) {
      reset({
        ...customer,
        dob: customer.dob ? moment(customer.dob).format(DOB_FORMAT) : '',
        phoneNumber: customer.mobilePhone || customer.landlinePhone || '+44',
        phoneType:
          customer.landlinePhone && !customer.mobilePhone
            ? EUserPhoneType.HOME
            : EUserPhoneType.MOBILE,
      });
    }
  }, [customer, reset]);

  const onSubmit = useCallback(
    (values: TFormValues) => {
      dispatch(
        patchCustomer({
          data: {
            ...values,
            dob: moment(values.dob, DOB_FORMAT).format(DATE_SERVER_FORMAT),
            // surgeryUuid: values.surgery?.uuid,
          },
          phone: {
            type: values.phoneType,
            number: values.phoneNumber,
            preferred: false,
          },
          onSuccess: () => {
            dispatch(ActionTypes.setCurrentForm(formUuid));
          },
        }),
      );
    },
    [formUuid, dispatch],
  );

  const onBack = () => {
    dispatch(signOutPatient());
  };

  return (
    <Page className="continue-as-guest">
      <PageHeader
        isScrolledFromTop={isScrolledFromTop}
        title={intl.get('forms.beforeWeGetStarted')}
        description={intl.get('forms.theInformationWillBeUsed')}
      />
      <div className="page-content">
        <main ref={mainRef} id="page-main-scrollable" className="page-main">
          <FormProvider {...formMethods}>
            <Form className="guest-form" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
              {!customer?.firstName && (
                <Controller
                  control={control}
                  name="firstName"
                  render={({ field, fieldState: { error } }) => (
                    <FormGroup className="form-floating-label">
                      <Label>{intl.get(`common.firstName`)} *</Label>
                      <Input
                        {...field}
                        placeholder={intl.get(`common.enterFirstName`)}
                        autoComplete="one-time-code"
                        role="presentation"
                        bsSize="lg"
                      />
                      {!!error && <FormText color="danger">{error.message}</FormText>}
                    </FormGroup>
                  )}
                />
              )}
              {!customer?.lastName && (
                <Controller
                  control={control}
                  name="lastName"
                  render={({ field, fieldState: { error } }) => (
                    <FormGroup className="form-floating-label">
                      <Label>{intl.get(`common.lastName`)} *</Label>
                      <Input
                        {...field}
                        placeholder={intl.get(`common.enterLastName`)}
                        autoComplete="one-time-code"
                        bsSize="lg"
                      />
                      {!!error && <FormText color="danger">{error.message}</FormText>}
                    </FormGroup>
                  )}
                />
              )}
              {!customer?.dob && (
                <Controller
                  control={control}
                  name="dob"
                  render={({ field, fieldState: { error } }) => (
                    <FormGroup className="form-floating-label">
                      <Label>{intl.get(`common.dateOfBirth`)} *</Label>
                      <Input
                        {...field}
                        type="text"
                        placeholder={intl.get(`common.dateOfBirthPlaceholder`)}
                        autoComplete="one-time-code"
                        mask="99/99/9999"
                        tag={InputMask}
                        bsSize="lg"
                      />
                      {!!error && <FormText color="danger">{error.message}</FormText>}
                    </FormGroup>
                  )}
                />
              )}
              {!customer?.gender && (
                <Controller
                  name="gender"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <FormGroup className="form-floating-label">
                      <Label>{intl.get(`common.gender`)} *</Label>
                      <Select<{ name: EGender }>
                        options={Object.values(EGender).map(g => ({
                          name: g,
                        }))}
                        optionLabel={option => intl.get(`common.${option.name}`)}
                        value={field.value ? { name: field.value as EGender } : undefined}
                        block
                        size="small"
                        onChange={val => {
                          const { name } = val as { name: EGender };
                          field.onChange(name);
                        }}
                        isOptionSelected={(option, select) => option.name === select[0]?.name}
                      />
                      {!!error && <FormText color="danger">{error.message}</FormText>}
                    </FormGroup>
                  )}
                />
              )}

              {(!customer?.address?.streetAddress ||
                !customer?.address?.city ||
                !customer?.address?.country ||
                !customer?.address?.postalCode) && (
                <>
                  <Controller
                    name="address.streetAddress"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormGroup className="form-floating-label">
                        <Label>{intl.get(`common.addressLine1`)} *</Label>
                        <Input
                          placeholder={intl.get(`common.addressLine1`)}
                          bsSize="lg"
                          {...field}
                        />
                        {!!error && <FormText color="danger">{error.message}</FormText>}
                      </FormGroup>
                    )}
                  />
                  <Controller
                    name="address.address2"
                    control={control}
                    render={({ field }) => (
                      <FormGroup className="form-floating-label flex-1">
                        <Label>{intl.get(`common.addressLine2`)}</Label>
                        <Input
                          placeholder={intl.get(`common.addressLine2`)}
                          bsSize="lg"
                          {...field}
                        />
                      </FormGroup>
                    )}
                  />
                  <Controller
                    name="address.suit"
                    control={control}
                    render={({ field }) => (
                      <FormGroup className="form-floating-label flex-1">
                        <Label>{intl.get(`common.flatSuite`)}</Label>
                        <Input placeholder={intl.get(`common.flatSuite`)} bsSize="lg" {...field} />
                      </FormGroup>
                    )}
                  />
                  <Controller
                    name="address.city"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormGroup className="form-floating-label flex-1">
                        <Label>{intl.get(`common.city`)} *</Label>
                        <Input placeholder={intl.get(`common.city`)} bsSize="lg" {...field} />
                        {!!error && <FormText color="danger">{error.message}</FormText>}
                      </FormGroup>
                    )}
                  />
                  <Controller
                    name="address.country"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormGroup className="form-floating-label flex-1">
                        <Label>{intl.get(`common.country`)} *</Label>
                        <Input placeholder={intl.get(`common.country`)} bsSize="lg" {...field} />
                        {!!error && <FormText color="danger">{error.message}</FormText>}
                      </FormGroup>
                    )}
                  />
                  <Controller
                    control={control}
                    name="address.postalCode"
                    render={({ field, fieldState: { error } }) => (
                      <FormGroup className="form-floating-label">
                        <Label>{intl.get(`common.postalCode`)} *</Label>
                        <Input
                          {...field}
                          placeholder={intl.get(`common.enterPostalCode`)}
                          autoComplete="one-time-code"
                          autoCapitalize="characters"
                          bsSize="lg"
                          onChange={e => {
                            field.onChange(e.target.value ? e.target.value.toUpperCase() : '');
                          }}
                        />
                        {!!error && <FormText color="danger">{error.message}</FormText>}
                      </FormGroup>
                    )}
                  />
                </>
              )}

              {!customer?.nhs && (
                <Controller
                  control={control}
                  name="nhs"
                  render={({ field, fieldState: { error } }) => (
                    <FormGroup className="form-floating-label">
                      <Label>{intl.get(`common.nhsNumber`)}</Label>
                      <Input
                        {...field}
                        placeholder={intl.get(`common.enterNHSNumber`)}
                        autoComplete="one-time-code"
                        type="number"
                        bsSize="lg"
                      />
                      {!!error && <FormText color="danger">{error.message}</FormText>}
                    </FormGroup>
                  )}
                />
              )}
              {!customer?.email && (
                <Controller
                  control={control}
                  name="email"
                  render={({ field, fieldState: { error } }) => (
                    <FormGroup className="form-floating-label">
                      <Label>{intl.get(`common.email`)} *</Label>
                      <Input
                        {...field}
                        placeholder={intl.get('common.emailPlaceholder')}
                        autoComplete="one-time-code"
                        type="email"
                        bsSize="lg"
                      />
                      {!!error && <FormText color="danger">{error.message}</FormText>}
                    </FormGroup>
                  )}
                />
              )}
              {!customer?.mobilePhone && !customer?.landlinePhone && (
                <div className="flex gap-md">
                  <Controller
                    control={control}
                    name="phoneType"
                    render={({ field: { value, onChange } }) => (
                      <FormGroup className="form-floating-label">
                        <Label>{intl.get(`common.type`)}</Label>
                        <Select<{ type: EUserPhoneType }>
                          options={[{ type: EUserPhoneType.MOBILE }, { type: EUserPhoneType.HOME }]}
                          optionLabel={option => intl.get(`common.phoneType.${option.type}`)}
                          value={value ? { type: value } : undefined}
                          block
                          onChange={val => {
                            const { type } = val as { type: EUserPhoneType };
                            onChange(type);
                          }}
                          isOptionSelected={(option, select) => option.type === select[0]?.type}
                        />
                      </FormGroup>
                    )}
                  />
                  <Controller
                    control={control}
                    name="phoneNumber"
                    render={({ field, fieldState: { error } }) => (
                      <FormGroup className="flex-1 form-floating-label">
                        <Label>{intl.get(`common.phoneNumber`)} *</Label>
                        <PhoneInput
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder={intl.get('common.enterPhone')}
                          autoComplete="one-time-code"
                          bsSize="lg"
                        />
                        {!!error && <FormText color="danger">{error.message}</FormText>}
                      </FormGroup>
                    )}
                  />
                </div>
              )}
              {/* {(formUuid === FORM_UUID_WEIGHT_LOSS_CLINIC ||
                formUuid === FORM_UUID_GENERAL_PRESCREENING) &&
                !customer?.surgery && (
                  <Controller
                    name="surgery"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormGroup className="form-floating-label">
                        <Label>{intl.get(`common.gpSurgery`)} *</Label>
                        <Select<ISurgery>
                          value={field.value as ISurgery}
                          placeholder={intl.get(`common.gpSurgery`)}
                          onChange={option => {
                            field.onChange(option);
                          }}
                          isSearchable
                          menuWidth="100%"
                          optionLabel={({ title, address }) => (
                            <div className="surgery-select-item">
                              <div className="surgery-select-item-info">
                                <div className="surgery-select-item__title">{title}</div>
                                <div className="surgery-select-item__address">
                                  {locationAddressRenderHelper(address)}
                                </div>
                              </div>
                            </div>
                          )}
                          isOptionSelected={({ uuid }, select) =>
                            select[0] && uuid === select[0].uuid
                          }
                          block
                          showSearchPlaceholder
                          isAsync
                          urlRequest="/surgeries"
                        />
                        {!!error && <FormText color="danger">{error.message}</FormText>}
                      </FormGroup>
                    )}
                  />
                )} */}
            </Form>
          </FormProvider>

          <PageFooter className="page-footer--scrollable">
            <Button color="primary" outline type="button" block onClick={onBack}>
              <Icon iconName="arrow-left" />
              {intl.get('button.back')}
            </Button>
            <Button
              color="primary"
              type="button"
              disabled={isLoading}
              block
              onClick={handleSubmit(onSubmit)}
            >
              {isLoading && <Spinner size="sm" />}
              {intl.get('button.continue')}
            </Button>
          </PageFooter>
        </main>
      </div>
    </Page>
  );
};

export default PatientInfo;
